import { Fragment, useCallback, useEffect, useMemo, useRef } from 'react'
import type { LayerProps, MapboxGeoJSONFeature } from 'react-map-gl'
import { Layer, Source, useMap } from 'react-map-gl'
import { useLocation } from 'react-router'
import { toast } from 'sonner'
import type { MapStyleValue } from '~/atoms'
import {
  aFiltersIsOpen,
  aLayersIsOpen,
  aMapStyle,
  aParcelDataVisible,
  aVisibleGeographies,
  useAtomValue,
} from '~/atoms'
import { useGeographies } from '~/hooks/map'
import { cn } from '~/lib'
import type { Client } from '~/models'

type GeographiesLayerProps = {
  client: Client
}

type MouseEvent = mapboxgl.MapMouseEvent & {
  features?: MapboxGeoJSONFeature[] | undefined
} & mapboxgl.EventData

const colors = [
  '#9333ea',
  '#a16207',
  // '#4338ca',
  '#b91c1c',
  '#0284c7',
  '#3f3f46',
  '#0d9488',
] as const

function getLayerStyles(
  index: number,
  type: string,
  beforeId: string | undefined,
  style: MapStyleValue
) {
  const color = colors[index % colors.length]

  const layerStyle: LayerProps = {
    type: 'line',
    beforeId: beforeId,
    paint: {
      'line-color': color,
      'line-width': [
        'case',
        ['boolean', ['feature-state', 'selected'], false],
        style === 'satellite' ? 3 : 2,
        style === 'satellite' ? 2 : 1,
      ],
    },
  }

  const layerStyleFill: LayerProps = {
    id: `geographies-fill-${type}`,
    interactive: true,
    beforeId: beforeId,
    type: 'fill',
    paint: {
      'fill-color': color,
      'fill-opacity': [
        'case',
        [
          'any',
          ['boolean', ['feature-state', 'hover'], false],
          ['boolean', ['feature-state', 'selected'], false],
        ],
        0.15,
        0.05,
      ],
    },
  }

  return { layerStyle, layerStyleFill }
}

export default function MapGeographiesLayer({ client }: GeographiesLayerProps) {
  const { geographies, fileGeographies } = useGeographies(client)
  const mapRef = useMap()
  const mapStyle = useAtomValue(aMapStyle)
  const visibleGeographies = useAtomValue(aVisibleGeographies)
  const parcelDataVisible = useAtomValue(aParcelDataVisible)
  const layersIsOpen = useAtomValue(aLayersIsOpen)
  const filtersIsOpen = useAtomValue(aFiltersIsOpen)
  const location = useLocation()

  const hoverTimeouts = useRef<Record<string, NodeJS.Timeout>>({})
  const isHovering = useRef<Record<string, string>>({})
  const isSelected = useRef<Record<string, string>>({})
  const isListingClicked = useRef(false)

  const filteredGeographies = useMemo(() => {
    return geographies.filter((geography) => {
      return visibleGeographies.includes(geography.type)
    })
  }, [geographies, visibleGeographies])

  const filteredFileGeographies = useMemo(() => {
    return fileGeographies.filter((geojson) => {
      return visibleGeographies.includes(geojson.properties.id)
    })
  }, [fileGeographies, visibleGeographies])

  const geographyStyles = useMemo(() => {
    const beforeId = mapRef.current?.getLayer('property-clusters')
      ? 'property-clusters'
      : 'z-index-2'

    let index = 0
    const styles: Record<
      string,
      { layerStyle: LayerProps; layerStyleFill: LayerProps }
    > = {}
    geographies.forEach((geography) => {
      styles[geography.type] = getLayerStyles(
        index++,
        geography.type,
        beforeId,
        mapStyle
      )
    })
    fileGeographies.forEach((geojson) => {
      styles[geojson.properties.id] = getLayerStyles(
        index++,
        geojson.properties.id,
        beforeId,
        mapStyle
      )
    })
    return styles
  }, [geographies, fileGeographies, mapRef.current, mapStyle])

  const onPropertyClick = useCallback((e: MouseEvent) => {
    if (e.features && e.features.length > 0) {
      isListingClicked.current = true
    } else {
      isListingClicked.current = false
    }
  }, [])

  useEffect(() => {
    toast.dismiss()
  }, [layersIsOpen, filtersIsOpen])

  useEffect(() => {
    if (location.pathname.includes('/cards')) {
      toast.dismiss()
    }
  }, [location.pathname])

  const onMouseClick = useCallback(
    (title: string, sourceId: string) => (e: MouseEvent) => {
      const feature = e.features?.[0]
      if (!feature || !feature.id) return
      if (isListingClicked.current) {
        return
      }

      const clearSourceSelected = () => {
        if (isSelected.current[sourceId]) {
          mapRef.current!.setFeatureState(
            {
              source: sourceId,
              id: isSelected.current[sourceId],
            },
            { selected: false }
          )
          delete isSelected.current[sourceId]
        }
      }
      clearSourceSelected()

      isSelected.current[sourceId] = feature.id.toString()
      mapRef.current!.setFeatureState(
        {
          source: sourceId,
          id: feature.id,
        },
        { selected: true }
      )

      const toastId = `hover-${feature.layer.id}`
      toast(
        () => {
          return (
            <GeoToastContent
              title={title}
              feature={feature}
              onMouseEnter={() => {
                Object.keys(hoverTimeouts.current).forEach((k) => {
                  clearTimeout(hoverTimeouts.current[k])
                  delete hoverTimeouts.current[k]
                })
                isHovering.current = {}
              }}
            />
          )
        },
        {
          id: toastId,
          duration: Infinity, //30_000,
          closeButton: true,
          position: 'top-right',
          style: {
            marginTop: '3rem',
            marginRight: '3rem',
          },
          onDismiss: () => clearSourceSelected(),
        }
      )
    },
    []
  )

  const onMouseMove = useCallback(
    (sourceId: string) => (e: MouseEvent) => {
      isListingClicked.current = false

      const feature = e.features?.[0]
      if (!feature || !feature.id) return

      mapRef.current!.getMap().getCanvasContainer().style.cursor = 'pointer'

      if (isHovering.current[sourceId]) {
        mapRef.current!.setFeatureState(
          {
            source: sourceId,
            id: isHovering.current[sourceId],
          },
          { hover: false }
        )
      }

      mapRef.current!.setFeatureState(
        {
          source: sourceId,
          id: feature.id,
        },
        { hover: true }
      )

      isHovering.current[sourceId] = feature.id.toString()
    },
    []
  )

  const onMouseLeave = useCallback(
    (sourceId: string) => () => {
      mapRef.current!.getMap().getCanvasContainer().style.cursor = 'grab'

      if (isHovering.current[sourceId]) {
        mapRef.current!.setFeatureState(
          {
            source: sourceId,
            id: isHovering.current[sourceId],
          },
          { hover: false }
        )
        delete isHovering.current[sourceId]
      }
    },
    []
  )

  useEffect(() => {
    if (mapRef.current) {
      const map = mapRef.current.getMap()

      map.on('click', 'property-single-clusters', onPropertyClick)

      filteredGeographies.forEach((geography) => {
        map.on(
          'click',
          `geographies-fill-${geography.type}`,
          onMouseClick(geography.label, `geographies-${geography.type}`)
        )
        map.on(
          'mousemove',
          `geographies-fill-${geography.type}`,
          onMouseMove(`geographies-${geography.type}`)
        )
        map.on(
          'mouseleave',
          `geographies-fill-${geography.type}`,
          onMouseLeave(`geographies-${geography.type}`)
        )
      })

      filteredFileGeographies.forEach((geojson) => {
        map.on(
          'click',
          `geographies-fill-${geojson.properties.id}`,
          onMouseClick(
            geojson.properties.name,
            `geographies-${geojson.properties.id}`
          )
        )
        map.on(
          'mousemove',
          `geographies-fill-${geojson.properties.id}`,
          onMouseMove(`geographies-${geojson.properties.id}`)
        )
        map.on(
          'mouseleave',
          `geographies-fill-${geojson.properties.id}`,
          onMouseLeave(`geographies-${geojson.properties.id}`)
        )
      })
    }

    return () => {
      if (
        mapRef.current &&
        (filteredGeographies.length > 0 || filteredFileGeographies.length > 0)
      ) {
        const map = mapRef.current.getMap()

        filteredGeographies.forEach((geography) => {
          map.off(
            'click',
            `geographies-fill-${geography.type}`,
            onMouseClick(geography.label, `geographies-${geography.type}`)
          )
          map.off(
            'mousemove',
            `geographies-fill-${geography.type}`,
            onMouseMove(`geographies-${geography.type}`)
          )
          map.off(
            'mouseleave',
            `geographies-fill-${geography.type}`,
            onMouseLeave(`geographies-${geography.type}`)
          )
        })
        filteredFileGeographies.forEach((geojson) => {
          map.off(
            'click',
            `geographies-fill-${geojson.properties.id}`,
            onMouseClick(
              geojson.properties.name,
              `geographies-${geojson.properties.id}`
            )
          )
          map.off(
            'mousemove',
            `geographies-fill-${geojson.properties.id}`,
            onMouseMove(`geographies-${geojson.properties.id}`)
          )
          map.off(
            'mouseleave',
            `geographies-fill-${geojson.properties.id}`,
            onMouseLeave(`geographies-${geojson.properties.id}`)
          )
        })
      }
    }
  }, [mapRef.current, filteredGeographies, filteredFileGeographies])

  if (parcelDataVisible) {
    return null
  }

  return (
    <>
      {filteredFileGeographies.map((geojson) => {
        const { layerStyle, layerStyleFill } =
          geographyStyles[geojson.properties.id]
        return (
          <Source
            key={geojson.properties.id}
            id={`geographies-${geojson.properties.id}`}
            type="geojson"
            data={geojson}
            promoteId={'id'}>
            <Layer {...layerStyle} />
            {!geojson.properties.disableHover && <Layer {...layerStyleFill} />}
          </Source>
        )
      })}
      {filteredGeographies.map((geography) => {
        const { layerStyle, layerStyleFill } = geographyStyles[geography.type]
        return (
          <Fragment key={geography.type}>
            <Source
              id={`geographies-${geography.type}`}
              type="geojson"
              data={geography.geojson}
              promoteId={'id'}>
              <Layer {...layerStyle} />
              <Layer {...layerStyleFill} />
            </Source>
          </Fragment>
        )
      })}
    </>
  )
}

function GeoToastContent({
  title,
  feature,
  onMouseEnter,
}: {
  title: string
  feature: MapboxGeoJSONFeature
  onMouseEnter: () => void
}) {
  const data: Record<string, any> = feature.properties?.data
    ? JSON.parse(feature.properties.data)
    : feature.properties
  return (
    <div onMouseEnter={onMouseEnter}>
      <strong>{title}</strong>

      <dl className="-mr-4 mt-3 max-h-56 overflow-y-auto text-xs capitalize">
        {Object.entries(data)
          .filter(([key, value]) => value && key !== 'id')
          .map(([key, value]) => {
            if (key === 'state_abbr' || key.includes('_fp')) return
            return (
              <div key={key} className="mb-1 grid grid-cols-3 gap-2">
                <dt className="col-span-1 font-medium leading-tight">{key}:</dt>
                <dd
                  className={cn(
                    'col-span-2',
                    !value.toString().startsWith('http') && 'capitalize'
                  )}>
                  {value}
                </dd>
              </div>
            )
          })}
      </dl>
    </div>
  )
}
